// src/pages/Profile.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Kullanıcı Profil Sayfası  —  /profile/:uid
// Firebase Realtime DB: /publicProfiles/$uid
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useParams, useNavigate }      from "react-router-dom";
import { ref, onValue }                from "firebase/database";
import { db }                          from "../firebase/config";
import { useAuth }                     from "../context/AuthContext";
import { useTranslation }              from "react-i18next";

// ── Renk sabitleri (BoneBeat ile aynı altın tema) ────────────────────────────
const C = {
  bg:         "#0d0a05",
  bgCard:     "#12100a",
  bgDeep:     "#0a0803",
  border:     "rgba(212,168,83,0.12)",
  borderHov:  "rgba(212,168,83,0.32)",
  gold:       "#d4a853",
  goldDim:    "rgba(212,168,83,0.5)",
  goldGhost:  "var(--bone-text-secondary)",
  goldFaint:  "rgba(212,168,83,0.08)",
  ivory:      "#e8d5b0",
  ivoryDim:   "rgba(232,213,176,0.6)",
  ivoryGhost: "rgba(232,213,176,0.25)",
  red:        "#e05c5c",
};

// ── Zaman yardımcısı ─────────────────────────────────────────────────────────
function fullDate(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
}

// ── Altın ayırıcı ─────────────────────────────────────────────────────────────
function GoldRule() {
  return (
    <div style={{
      height: 1, margin: "20px 0",
      background: `linear-gradient(90deg, transparent, ${C.border}, transparent)`,
    }} />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANA BILEŞEN
// ─────────────────────────────────────────────────────────────────────────────

export default function Profile() {
  const { uid }          = useParams();
  const navigate         = useNavigate();
  const { user: me, isAdmin } = useAuth();
  const { t }            = useTranslation();

  const [profile,   setProfile]   = useState(null);   // Firebase'den gelen profil
  const [loading,   setLoading]   = useState(true);
  const [notFound,  setNotFound]  = useState(false);
  const [dbError,   setDbError]   = useState(null);

  // Firebase'den profil verisi çek
  useEffect(() => {
    if (!uid) { setNotFound(true); setLoading(false); return; }

    setLoading(true);
    setDbError(null);
    setNotFound(false);

    const unsub = onValue(
      ref(db, `publicProfiles/${uid}`),
      (snap) => {
        if (snap.exists()) {
          setProfile({ uid, ...snap.val() });
        } else {
          setNotFound(true);
        }
        setLoading(false);
      },
      (err) => {
        console.error("[Profile] Firebase okuma hatası:", err.code, err.message);
        setDbError(err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [uid]);

  const isOwnProfile = me?.uid === uid;

  // ── İsmin baş harfi (avatar fallback) ────────────────────────────────────
  const initial = profile?.displayName
    ? profile.displayName.trim()[0].toUpperCase()
    : "?";

  // ── Yükleniyor ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%", margin: "0 auto 16px",
            border: "2px solid rgba(212,168,83,0.15)",
            borderTopColor: "rgba(212,168,83,0.55)",
            animation: "prf-spin 0.9s linear infinite",
          }} />
          <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, color: C.goldGhost, letterSpacing: "0.2em" }}>
            {t('common.loading')}
          </p>
          <style>{`@keyframes prf-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // ── Bulunamadı ────────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
        <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 64, fontWeight: 900, fontStyle: "italic", color: "rgba(212,168,83,0.15)" }}>
          404
        </span>
        <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 11, letterSpacing: "0.28em", color: C.goldGhost, textTransform: "uppercase" }}>
          {t('profile.notFound')}
        </p>
        <button
          onClick={() => navigate("/")}
          style={{
            fontFamily: "'Courier Prime',monospace", fontSize: 10,
            letterSpacing: "0.2em", textTransform: "uppercase",
            color: C.goldDim, background: "transparent",
            border: `1px solid ${C.border}`, padding: "8px 20px",
            cursor: "pointer", marginTop: 8, transition: "all 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.gold; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = C.goldDim; }}
        >
          {t('notFound.backHome')}
        </button>
      </div>
    );
  }

  // ── Firebase okuma hatası ─────────────────────────────────────────────────
  if (dbError) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: 400, padding: "0 24px" }}>
          <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: C.red, marginBottom: 10 }}>
            {t('bonebeat.errorTitle', 'Bağlantı Hatası')}
          </p>
          <p style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, color: "rgba(224,92,92,0.5)", marginBottom: 20, wordBreak: "break-word" }}>
            {dbError}
          </p>
          <button onClick={() => navigate(-1)} style={{ fontFamily: "'Courier Prime',monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--bone-text-accent)", background: "transparent", border: `1px solid ${C.border}`, padding: "8px 18px", cursor: "pointer" }}>
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  // ── Profil içeriği ────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "0 0 60px" }}>

      {/* ── Geri butonu ── */}
      <div style={{ padding: "28px 32px 0" }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "'Courier Prime',monospace", fontSize: 10,
            letterSpacing: "0.2em", textTransform: "uppercase",
            color: C.gold, display: "flex", alignItems: "center", gap: 6,
            padding: 0, transition: "color 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = C.goldDim}
          onMouseLeave={e => e.currentTarget.style.color = C.gold}
        >
          ← {t('common.back')}
        </button>
      </div>

      <div style={{ maxWidth: 680, margin: "0 auto", padding: "32px 24px 0" }}>

        {/* ════════════════════════════════════════════
            PROFİL KARTI
        ════════════════════════════════════════════ */}
        <div style={{
          background: C.bgCard,
          border: `1px solid ${C.border}`,
          padding: "36px 36px 28px",
          position: "relative", overflow: "hidden",
          marginBottom: 0,
        }}>
          {/* Arka plan parıltısı */}
          <div style={{
            position: "absolute", top: -50, right: -50,
            width: 200, height: 200, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(212,168,83,0.05) 0%, transparent 70%)",
            pointerEvents: "none",
          }} />

          {/* ── Avatar + Temel Bilgiler ── */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>

            {/* Avatar */}
            <div style={{
              width: 80, height: 80, borderRadius: "50%", flexShrink: 0,
              border: `2px solid ${C.border}`,
              background: C.bgDeep, overflow: "hidden",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
            }}>
              {profile.photoURL ? (
                <img
                  src={profile.photoURL} alt={profile.displayName || "Avatar"}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 32, fontWeight: 700, color: C.goldDim,
                }}>
                  {initial}
                </span>
              )}
            </div>

            {/* İsim + meta */}
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                <h1 style={{
                  fontFamily: "'Playfair Display',serif",
                  fontSize: 26, fontWeight: 700, color: C.ivory,
                  margin: 0, letterSpacing: "0.04em",
                }}>
                  {profile.displayName || t('bonebeat.anonymousUser')}
                </h1>

                {/* "Sen" etiketi */}
                {isOwnProfile && (
                  <span style={{
                    fontFamily: "'Courier Prime',monospace", fontSize: 8,
                    letterSpacing: "0.22em", textTransform: "uppercase",
                    color: C.goldDim, border: `1px solid ${C.goldDim}`,
                    padding: "2px 7px",
                  }}>
                    {t('admin.you')}
                  </span>
                )}

                {/* Admin rozeti */}
                {isOwnProfile && isAdmin && (
                  <span style={{
                    fontFamily: "'Courier Prime',monospace", fontSize: 8,
                    letterSpacing: "0.22em", textTransform: "uppercase",
                    color: "rgba(224,92,92,0.7)",
                    border: "1px solid rgba(224,92,92,0.3)",
                    padding: "2px 7px",
                  }}>
                    Admin
                  </span>
                )}
              </div>

              {/* BoneCast Arşivleri etiketi */}
              <p style={{
                fontFamily: "'Courier Prime',monospace",
                fontSize: 9, color: C.goldGhost,
                letterSpacing: "0.18em", textTransform: "uppercase",
                marginBottom: 0,
              }}>
                BoneCast Arşivleri
              </p>
            </div>
          </div>

          <GoldRule />

          {/* ── Bio + Uyruk ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

            {/* Bio */}
            {profile.bio ? (
              <div>
                <div style={{
                  fontFamily: "'Courier Prime',monospace", fontSize: 9,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  color: C.goldGhost, marginBottom: 8,
                }}>
                  ✦ {t('settings.section.profile')}
                </div>
                <p style={{
                  fontFamily: "'Courier Prime',monospace",
                  fontSize: 12, color: C.ivoryDim,
                  lineHeight: 1.85, letterSpacing: "0.04em",
                  borderLeft: `2px solid rgba(212,168,83,0.18)`,
                  paddingLeft: 12, margin: 0,
                }}>
                  {profile.bio}
                </p>
              </div>
            ) : (
              /* Bio yok placeholder */
              <div>
                <div style={{
                  fontFamily: "'Courier Prime',monospace", fontSize: 9,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  color: C.goldGhost, marginBottom: 8,
                }}>
                  ✦ {t('settings.section.profile')}
                </div>
                <p style={{
                  fontFamily: "'Courier Prime',monospace",
                  fontSize: 11, color: "rgba(212,168,83,0.2)",
                  fontStyle: "italic", letterSpacing: "0.06em",
                }}>
                  {t('profile.noBio')}
                </p>
              </div>
            )}

            {/* Uyruk */}
            {profile.nationality && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  fontFamily: "'Courier Prime',monospace", fontSize: 9,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  color: C.goldGhost, flexShrink: 0,
                }}>
                  {t('settings.nationality.label')}
                </div>
                <div style={{
                  height: 1, flex: 1,
                  background: C.border,
                }} />
                <span style={{
                  fontFamily: "'Courier Prime',monospace",
                  fontSize: 11, color: C.goldDim,
                  letterSpacing: "0.1em",
                }}>
                  {profile.nationality}
                </span>
              </div>
            )}

            {/* Katılım tarihi */}
            {profile.createdAt && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  fontFamily: "'Courier Prime',monospace", fontSize: 9,
                  letterSpacing: "0.22em", textTransform: "uppercase",
                  color: C.goldGhost, flexShrink: 0,
                }}>
                  {t('profile.joined')}
                </div>
                <div style={{ height: 1, flex: 1, background: C.border }} />
                <span style={{
                  fontFamily: "'Courier Prime',monospace",
                  fontSize: 11, color: "rgba(212,168,83,0.35)",
                  letterSpacing: "0.08em",
                }}>
                  {fullDate(profile.createdAt)}
                </span>
              </div>
            )}
          </div>

          {/* ── Ayarlar kısayolu (sadece kendi profilinde) ── */}
          {isOwnProfile && (
            <>
              <GoldRule />
              <button
                onClick={() => navigate("/settings")}
                style={{
                  fontFamily: "'Courier Prime',monospace", fontSize: 10,
                  letterSpacing: "0.2em", textTransform: "uppercase",
                  color: C.goldDim, background: "transparent",
                  border: `1px solid ${C.border}`,
                  padding: "9px 20px", cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.borderHov; e.currentTarget.style.color = C.gold; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border;    e.currentTarget.style.color = C.goldDim; }}
              >
                ⚙ {t('settings.section.profile')} {t('common.edit')}
              </button>
            </>
          )}
        </div>

      </div>

      {/* Scrollbar */}
      <style>{`
        ::-webkit-scrollbar { width: 4px; background: ${C.bgDeep}; }
        ::-webkit-scrollbar-thumb { background: rgba(212,168,83,0.15); }
        ::selection { background: rgba(212,168,83,0.2); color: ${C.ivory}; }
      `}</style>
    </div>
  );
}
